import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router';
import type { LoaderFunction, MetaFunction } from 'react-router';
import { ShopRepository } from '~/repositories/shop.server'
import {
  AddressModel,
  ShopDisplayIDModel,
  ShopModel, ShopModelJSON,
  FileModel, FileModelJSON,
} from '~/models'
import { db } from '~/utils/db.server'
import { Button } from '~/components/buttons'
import { createMeta } from '~/utils/application'
import { getAppAssetsBaseURL } from '~/utils/application.server'
import { SelectingList } from '~/components/compounds/user/models/file/selecting-list'
import { TabMenu } from '~/components/templates/user/tabmenu'
import { Overview } from '~/components/compounds/user/models/shop/overview'
import { Detail } from '~/components/compounds/user/models/shop/detail'
import { ProductCard as BicycleCard } from '~/components/bicycle/cards'
import { ProductCard as PartCard } from '~/components/part/cards'
import { ProductCard as SupplyCard } from '~/components/supply/cards'

export const meta: MetaFunction<typeof loader> = (args => {
  const loaderData = args.data as LoaderData
  const shop = loaderData.data?.shop
  if (!shop?.address)
    throw new Error('ショップに住所が登録されていません。')
  const address = AddressModel.fromJSON(shop.address)
  return createMeta({
    ...args,
    title: `${address.prefectureCode.getDisplayName()}${address.city} | ${shop.name}`,
    description: shop.description,
    keywords: `${shop.name}`,
    isShortedTitle: true,
  })
})

type LoaderData = {
  data?: {
    shop: ShopModelJSON
    assetBaseURL: string
  }
  error?: Error
}

export const loader: LoaderFunction = async ({ params }) => {
  if (!params.displayID) throw new Error('ショップIDが指定されていません')
  const displayID = new ShopDisplayIDModel(params.displayID)
  const repository = new ShopRepository(db.shop, db.file)
  const shop = await repository.find({
    displayID: displayID,
    include: {
      products: true,
    },
  })
  return {
    data: {
      shop: shop,
      assetBaseURL: getAppAssetsBaseURL(),
    }
  }
}

export default function Shop() {
  const { data } = useLoaderData<LoaderData>()
  const [selectedFile, setSelectedFile] = useState<FileModelJSON>()
  const navigation = useNavigate()
  useEffect(() => {
    if (data?.shop?.files && data.shop.files.length > 0) {
      setSelectedFile(data.shop.files[0])
    }
  }, [data])
  if (
    !data?.assetBaseURL || !data?.shop?.files || !data?.shop?.address || !data?.shop?.contact
  ) return null

  return (
    <div className="flex flex-row flex-wrap">
      <div className="basis-full md:basis-1/2 flex flex-row flex-wrap overflow-hidden h-auto my-auto">
        {selectedFile &&
          <SelectingList
            files={data.shop.files.map(file => FileModel.fromJSON(file))}
            assetBaseURL={data.assetBaseURL}
            onClick={file => setSelectedFile(file.toJSON())}
            selected={FileModel.fromJSON(selectedFile)}
          />
        }
      </div>
      <div className="basis-full md:basis-1/2 p-2 mt-6 md:mt-4">
        <Overview shop={ShopModel.fromJSON(data.shop)} />
      </div>
      <TabMenu
        tabs={[
          {
            title: '店舗情報',
            content: <Detail shop={ShopModel.fromJSON(data.shop)} />
          },
          {
            title: '商品情報',
            content: (
              <>
                <h2 className="border-l-4 border-primary pl-2 text-lg">自転車</h2>
                <div className="flex flex-row flex-wrap w-auto my-2">
                  {data.shop.products && data.shop.products
                    .filter(e => e.type === 0)?.map(product => (
                      <div
                        key={product.displayID}
                        className="basis-1/2 sm:basis-1/3 md:basis-1/4 px-1"
                      >
                        <BicycleCard
                          key={product.displayID}
                          assetsBaseURL={data.assetBaseURL}
                          product={product}
                        />
                      </div>
                    ))}
                </div>
                <h2 className="border-l-4 border-primary pl-2 mt-6 text-lg">パーツ</h2>
                <div className="flex flex-row flex-wrap w-auto my-2">
                  {data.shop.products && data.shop.products
                    .filter(e => e.type === 1)?.map(product => (
                      <div
                        key={product.displayID}
                        className="basis-1/2 sm:basis-1/3 md:basis-1/4 px-1"
                      >
                        <PartCard
                          key={product.displayID}
                          assetsBaseURL={data.assetBaseURL}
                          product={product}
                        />
                      </div>
                    ))}
                </div>
                <h2 className="border-l-4 border-primary pl-2 mt-6 text-lg">用品</h2>
                <div className="flex flex-row flex-wrap w-auto my-2">
                  {data.shop.products && data.shop.products
                    .filter(e => e.type === 1)?.map(product => (
                      <div
                        key={product.displayID}
                        className="basis-1/2 sm:basis-1/3 md:basis-1/4 px-1"
                      >
                        <SupplyCard
                          key={product.displayID}
                          assetsBaseURL={data.assetBaseURL}
                          product={product}
                        />
                      </div>
                    ))}
                </div>
              </>
            )
          },
        ]}
      />
      <a className="basis-full flex justify-center my-8">
        <Button
          className="w-1/3"
          onClick={() => { navigation(-1) }}
        >
          戻る
        </Button>
      </a>
    </div>
  )
}
